/**
 * Razorpay checkout helper — loads SDK on demand and opens checkout.
 * Backend generates the order; frontend just presents the SDK.
 */
import api from "@/api/client";
import { toast } from "sonner";

let scriptPromise = null;

function loadRazorpayScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { scriptPromise = null; reject(new Error("Failed to load Razorpay SDK")); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Open Razorpay checkout.
 *
 * @param {Object} opts
 * @param {number} opts.amount   Amount in rupees
 * @param {"order_payment"|"wallet_topup"} opts.purpose
 * @param {string=} opts.orderId       ERP order id
 * @param {string=} opts.invoiceId     ERP invoice id
 * @param {Object=} opts.prefill       { name, email, contact }
 * @param {string=} opts.description
 * @param {Function=} opts.onSuccess
 */
export async function openRazorpayCheckout({ amount, purpose, orderId, invoiceId, prefill = {}, description, onSuccess, onDismiss }) {
  try {
    // 1) Ensure Razorpay is enabled & get public config
    const { data: cfg } = await api.get("/payments/config");
    if (!cfg.enabled) {
      toast.error("Razorpay is not enabled. Ask admin to configure it in Integrations.");
      return;
    }
    await loadRazorpayScript();

    // 2) Create Razorpay order on backend
    const { data: order } = await api.post("/payments/create-order", {
      amount,
      purpose,
      order_id: orderId || null,
      invoice_id: invoiceId || null,
    });

    // 3) Open checkout SDK
    const rzp = new window.Razorpay({
      key: cfg.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "FuelExpress",
      description: description || (purpose === "wallet_topup" ? "Wallet top-up" : "Order payment"),
      prefill,
      theme: { color: "#f97316" },
      handler: async (resp) => {
        try {
          const v = await api.post("/payments/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Payment successful ✓");
          if (onSuccess) onSuccess(v.data);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Payment verification failed");
        }
      },
      modal: {
        ondismiss: () => {
          toast.info("Payment cancelled");
          if (onDismiss) onDismiss();
        },
      },
    });
    rzp.open();
  } catch (e) {
    toast.error(e.response?.data?.detail || e.message || "Checkout error");
  }
}
